import {
    ChevronLeft,
    ChevronRight,
    Search,
    Plus,
    Bell,
    Menu,
    Users,
    Shield,
    Globe,
    AppWindow,
    Building2,
    Lock
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useLayout } from '@/contexts/layout-context';

import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { TenantSwitcher } from '../dashboard-layout/components/tenant-switcher';
import UserMenu from '@/features/security/components/user-menu';
import { CommandPalette } from '../../../wasm/CommandPalette';
import { useUsers } from '@/store/user';
import { useTenants } from '@/store/tenant';
import type { User, Tenant } from '@/types';

export const TopBar = () => {
    const { toggleSidebar, breadcrumbs } = useLayout();
    const navigate = useNavigate();
    const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const addMenuRef = useRef<HTMLDivElement>(null);

    const [pendingRequests, setPendingRequests] = useState<User[]>([]);
    const [tenantsList, setTenantsList] = useState<Tenant[]>([]);
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const notificationRef = useRef<HTMLDivElement>(null);

    const fetchPendingRequests = async () => {
        try {
            const users = await useUsers.getState().list(undefined, undefined, 'pending');
            setPendingRequests(users || []);
            const tenants = await useTenants.getState().list();
            setTenantsList(tenants || []);
        } catch (error) {
            console.error('Failed to fetch pending requests:', error);
        }
    };

    const getTenantName = (tenantId: string) => {
        const tenant = tenantsList.find(t => t.id === tenantId);
        return tenant ? tenant.name : 'Unknown Tenant';
    };

    useEffect(() => {
        fetchPendingRequests();
        const interval = setInterval(fetchPendingRequests, 60000); // 1 minute
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (addMenuRef.current && !addMenuRef.current.contains(event.target as Node)) {
                setIsAddMenuOpen(false);
            }
            if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
                setIsNotificationOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Listen for Ctrl+K / Cmd+K hotkey
    useEffect(() => {
        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setIsSearchOpen(true);
            }
        };
        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    }, []);

    const addOptions = [
        { label: 'Create Tenant', icon: Building2, to: '/m/tenants/create', color: 'text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300' },
        { label: 'Create User', icon: Users, to: '/m/users/create', color: 'text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300' },
        { label: 'Create Group', icon: Shield, to: '/m/groups/create', color: 'text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300' },
        { label: 'Create Application', icon: AppWindow, to: '/m/applications/create', color: 'text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300' },
        { label: 'Create IDP', icon: Globe, to: '/m/idps/create', color: 'text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300' },
        { label: 'Create Role', icon: Shield, to: '/m/roles/create', color: 'text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300' },
        { label: 'Create Permission', icon: Lock, to: '/m/permissions/create', color: 'text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300' },
    ];

    return (
        <header className="h-16 bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-4 lg:px-6 shrink-0 relative z-50">
            <div className="flex items-center gap-2 lg:gap-4 text-sm text-slate-500 dark:text-slate-400">
                <button
                    onClick={toggleSidebar}
                    className="lg:hidden p-2 hover:bg-slate-100 dark:hover:bg-slate-900 rounded-md transition-colors"
                >
                    <Menu className="w-5 h-5" />
                </button>

                <div className="hidden sm:flex items-center gap-1">
                    <button className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-900 rounded-md transition-colors">
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-900 rounded-md transition-colors">
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
                <nav className="flex items-center gap-2">
                    <span className="hidden md:inline hover:text-slate-900 dark:hover:text-slate-200 cursor-pointer">
                        <Link to="/">SmartSite</Link>
                    </span>
                    {breadcrumbs && breadcrumbs.map((crumb, index) => (
                        <React.Fragment key={crumb.name}>
                            <span className="hidden md:inline text-slate-300 dark:text-slate-700">/</span>
                            {index === breadcrumbs.length - 1 || !crumb.to ? (
                                <span className="font-medium text-(--primary) truncate max-w-30 sm:max-w-none">
                                    {crumb.name}
                                </span>
                            ) : (
                                <Link
                                    to={crumb.to}
                                    className="hidden md:inline hover:text-slate-900 dark:hover:text-slate-200 transition-colors"
                                >
                                    {crumb.name}
                                </Link>
                            )}
                        </React.Fragment>
                    ))}
                </nav>
            </div>

            <div className="flex items-center gap-2 lg:gap-4">
                <div 
                    className="relative hidden sm:block w-48 lg:w-72 cursor-pointer"
                    onClick={() => setIsSearchOpen(true)}
                >
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
                        <Search className="w-4 h-4" />
                    </span>
                    <input
                        type="text"
                        placeholder="Search... (Ctrl+K)"
                        readOnly
                        className="w-full pl-10 pr-12 py-1.5 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg dark:text-white transition-all outline-none cursor-pointer"
                    />
                    <div className="absolute inset-y-0 right-2 flex items-center">
                        <kbd className="hidden lg:inline-flex items-center gap-0.5 h-5 select-none rounded border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-1.5 font-mono text-[10px] font-medium text-slate-400">
                            <span>Ctrl</span><span>K</span>
                        </kbd>
                    </div>
                </div>

                <div className="relative" ref={addMenuRef}>
                    <Button
                        size="sm"
                        className="gap-2"
                        onClick={() => setIsAddMenuOpen(!isAddMenuOpen)}
                    >
                        <Plus className={`w-4 h-4 transition-transform duration-200 ${isAddMenuOpen ? 'rotate-45' : ''}`} />
                        <span className="hidden xs:inline">Add</span>
                    </Button>

                    {isAddMenuOpen && (
                        <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl py-2 z-50 animate-in fade-in zoom-in-95 duration-100">
                            <div className="px-3 py-2 mb-1 border-b border-slate-100 dark:border-slate-800">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Quick Actions</span>
                            </div>
                            {addOptions.map((option) => (
                                <button
                                    key={option.label}
                                    onClick={() => {
                                        navigate(option.to);
                                        setIsAddMenuOpen(false);
                                    }}
                                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors text-left"
                                >
                                    <div className={`p-1.5 rounded-lg ${option.color}`}>
                                        <option.icon className="w-4 h-4" />
                                    </div>
                                    <span className="font-medium">{option.label}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Tenant Switcher */}
                <TenantSwitcher />

                <div className="flex items-center gap-1 border-l border-slate-200 dark:border-slate-800 pl-2 lg:pl-4">
                    <div className="relative" ref={notificationRef}>
                        <button
                            onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 relative transition-colors"
                        >
                            <Bell className="w-5 h-5" />
                            {pendingRequests.length > 0 && (
                                <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 border-2 border-white dark:border-slate-950 rounded-full animate-pulse"></span>
                            )}
                        </button>

                        {isNotificationOpen && (
                            <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl py-1.5 z-50 animate-in fade-in zoom-in-95 duration-100 max-h-96 overflow-y-auto">
                                <div className="px-4 py-2 mb-1 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center whitespace-nowrap">
                                    <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Pending Approvals</span>
                                    {pendingRequests.length > 0 && (
                                        <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                                            {pendingRequests.length}
                                        </span>
                                    )}
                                </div>
                                {pendingRequests.length === 0 ? (
                                    <div className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
                                        No pending approval requests.
                                    </div>
                                ) : (
                                    <div className="divide-y divide-slate-50 dark:divide-slate-800/40">
                                        {Object.entries(
                                            pendingRequests.reduce((acc, user) => {
                                                const tenantId = user.tenant_id || 'unknown';
                                                if (!acc[tenantId]) acc[tenantId] = [];
                                                acc[tenantId].push(user);
                                                return acc;
                                            }, {} as Record<string, User[]>)
                                        ).map(([tenantId, users]) => (
                                            <button
                                                key={tenantId}
                                                onClick={() => {
                                                    setIsNotificationOpen(false);
                                                    navigate('/m/tasks');
                                                }}
                                                className="w-full px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors flex items-center justify-between text-left"
                                            >
                                                <div className="flex items-center gap-2 min-w-0 pr-2">
                                                    <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
                                                    <span className="font-semibold text-sm text-slate-700 dark:text-slate-200 truncate">
                                                        {getTenantName(tenantId)}
                                                    </span>
                                                </div>
                                                <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md shrink-0">
                                                    {users.length} pending
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <div className="mt-1 pt-1.5 border-t border-slate-100 dark:border-slate-800 px-3">
                                    <button
                                        onClick={() => {
                                            setIsNotificationOpen(false);
                                            navigate('/m/tasks');
                                        }}
                                        className="w-full text-center py-1.5 text-xs font-semibold text-(--primary) hover:underline"
                                    >
                                        View all tasks
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* User Menu */}
                    <UserMenu />
                </div>
            </div>
            
            <CommandPalette isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
        </header>
    );
};
